import { FC, useCallback, useMemo } from 'react'
import { StreamControlPreset, CONTROL_OFF, CONTROL_ON, ControlState } from 'components/features/coaching/advisorVideoCall/StreamControlPreset/StreamControlPreset'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { DeviceSelect } from 'components/features/coaching/advisorVideoCall/DeviceSelect/DeviceSelect'
import { AgoraMuteAudioAction, AgoraUnmuteAudioAction, AgoraAudioInputAction } from 'components/features/videoCalls/types/actions'
import { agoraInstances } from 'components/features/videoCalls/agoraInstances'
import { useDevices } from 'components/features/videoCalls/useDevices'
import { TestMicVolume } from 'components/features/coaching/advisorVideoCall/TestMic/TestMicVolume'

type MicControlProps = {
  isFullScreen: boolean
}
export const MicControl: FC<MicControlProps> = (props) => {
  const { isFullScreen } = props
  const muteState = useStoreSelector((state) => state.app.videoCall.localStream.audioMuteState)
  const clientState = useStoreSelector((state) => state.app.videoCall.client.state)
  const localStreamState = useStoreSelector((state) => state.app.videoCall.localStream.state)

  const storeDeviceId = useStoreSelector((state) => state.app.videoCall.localStream.audioInputDeviceId)
  const localStreamId = useStoreSelector((state) => state.app.videoCall.localStream.streamId)
  const stream = agoraInstances.streams.get(localStreamId as any)

  const devices = useDevices(stream, 'audioinput')
  const permissions = useStoreSelector((state) => state.app.videoCall.permissions.mic)

  const dispatch = useStoreDispatch()

  const handleDeviceChange = useCallback((value: string | null) => {
    if (value === null) {
      return
    }
    dispatch(new AgoraAudioInputAction(value))
  }, [dispatch])

  const handleClick = useCallback(() => {
    if (muteState === 'mute') {
      dispatch(new AgoraUnmuteAudioAction(localStreamId))
    } else if (muteState === 'unmute') {
      dispatch(new AgoraMuteAudioAction(localStreamId))
    }
  }, [muteState, dispatch, localStreamId])

  const devicesSelectChild = useMemo(() => {
    if (!devices || devices.length === 0) {
      return null
    }
    const options = devices.map(({ label, deviceId }, index) => ({
      label: label || `Device ${ index }`,
      value: deviceId,
    }))

    return (
      <DeviceSelect
        options={ options }
        value={ storeDeviceId }
        onChange={ handleDeviceChange }
        placeholder='Select Microphone'
      />
    )
  }, [devices, storeDeviceId, handleDeviceChange])

  const contentChild = useMemo(() => {
    if (permissions === 'denied') {
      return <span><strong>Microphone</strong><br />Check browser permissions</span>
    }

    if (devicesSelectChild) {
      return devicesSelectChild
    }

    return <span onClick={ handleClick }>Microphone</span>
  }, [permissions, devicesSelectChild, handleClick])

  const attrs = useMemo(() => {
    const nextAttrs = {
      state: CONTROL_OFF as ControlState,
      status: 'Off',
      isProgress: false,
      isDisabled: false,
    }

    if (clientState === 'progress') {
      nextAttrs.isDisabled = true
    }

    if (muteState === 'muting' || muteState === 'unmuting') {
      nextAttrs.isProgress = true
      nextAttrs.isDisabled = true
    }

    if (muteState === 'unmute' || muteState === 'unmuting') {
      nextAttrs.state = CONTROL_ON
      nextAttrs.status = 'On'
    }

    if (localStreamState === 'progress') {
      nextAttrs.isDisabled = true
      nextAttrs.isProgress = true
    }

    if (permissions === 'denied') {
      nextAttrs.status = 'Microphone unavailable'
      nextAttrs.isDisabled = true
      nextAttrs.state = CONTROL_OFF
      nextAttrs.status = 'Off'
    }

    return nextAttrs
  }, [muteState, permissions, clientState, localStreamState])

  return (
    <StreamControlPreset
      { ...attrs }
      isFullScreen={ isFullScreen }
      offIconSlug='mute-mic'
      onIconSlug='mic'
      onClick={ handleClick }
      meter={ <TestMicVolume /> }
    >
      { contentChild }
    </StreamControlPreset>
  )
}


